import { createContext, useContext, useEffect, useReducer } from "react";
import axios from "../../api/axios";
import { LOADED, STATUS_NOT_LOADED } from "../../api/constant";
import { showTypeErrorFetch, alertSuccess, confirmDelete } from "../../utilities/alerts.utilities";
import { INITIAL_STATE, types, providersReducer } from "../../reducers/inventory/providersReducer";

export const ProvidersIndexContext = createContext({});

export const ProvidersIndexProvider = ({ children }) => {
  const [state, dispatch] = useReducer(providersReducer, INITIAL_STATE);

  const setFieldFormProvider = (value, field) => {
    dispatch({ type: types.SET_FIELD_FORM_PROVIDER, payload: { value, name: field } });
  };

  const goToProviderEdit = (item_id) => {
    dispatch({ type: types.SET_EDIT_FORM_PROVIDER, payload: true });
    const selected = state.elements.find((item) => item.id === item_id);
    if (selected) {
      dispatch({
        type: types.EDIT_FORM_PROVIDER,
        payload: {
          id: selected.id,
          name: selected.name ?? "",
          doc_num: selected.doc_num ?? "",
          phone: selected.phone ?? "",
          email: selected.email ?? "",
          address: selected.address ?? "",
          genre: selected.genre ?? "",
          city: selected.city ?? "",
        },
      });
    }
  };

  useEffect(() => {
    if (state.status === STATUS_NOT_LOADED) {
      axios()
        .get("inventory/getListProviders", { params: state.filter })
        .then(({ data }) => {
          dispatch({
            type: types.PROVIDERS_LOADED,
            payload: { elements: data.providers ?? [], rows: data.rows ?? 0 },
          });
        })
        .catch((e) => showTypeErrorFetch(e));
    }
  }, [state.status, state.filter.sortBy, state.filter.sortDir]);

  const createProviderClick = () => {
    axios()
      .post("inventory/createProvider", {
        name: state.edit_provider.name,
        doc_num: state.edit_provider.doc_num,
        phone: state.edit_provider.phone || null,
        email: state.edit_provider.email || null,
        address: state.edit_provider.address || null,
        genre: state.edit_provider.genre || null,
        city: state.edit_provider.city,
      })
      .then(() => {
        dispatch({ type: types.RELOAD_INDEX_PROVIDERS });
        dispatch({ type: types.EDIT_FORM_PROVIDER, payload: INITIAL_STATE.edit_provider });
        dispatch({ type: types.SET_EDIT_FORM_PROVIDER, payload: false });
        alertSuccess("Proveedor creado correctamente");
      })
      .catch((e) => showTypeErrorFetch(e));
  };

  const editProviderClick = () => {
    axios()
      .put(`inventory/editProvider/${state.edit_provider.id}`, {
        name: state.edit_provider.name,
        doc_num: state.edit_provider.doc_num,
        phone: state.edit_provider.phone || null,
        email: state.edit_provider.email || null,
        address: state.edit_provider.address || null,
        genre: state.edit_provider.genre || null,
        city: state.edit_provider.city,
      })
      .then(() => {
        dispatch({ type: types.RELOAD_INDEX_PROVIDERS });
        dispatch({ type: types.EDIT_FORM_PROVIDER, payload: INITIAL_STATE.edit_provider });
        dispatch({ type: types.SET_EDIT_FORM_PROVIDER, payload: false });
        alertSuccess("Proveedor editado correctamente");
      })
      .catch((e) => showTypeErrorFetch(e));
  };

  const assignProviderToCompanyClick = (provider_id) => {
    axios()
      .post(`inventory/provider/${provider_id}/assignCompany`)
      .then(({ data }) => {
        alertSuccess(data?.message ?? "Proveedor asignado a la compañía correctamente.");
        dispatch({ type: types.RELOAD_INDEX_PROVIDERS });
      })
      .catch((e) => showTypeErrorFetch(e));
  };

  const unassignProviderToCompanyClick = (provider_id) => {
    axios()
      .post(`inventory/provider/${provider_id}/unassignCompany`)
      .then(({ data }) => {
        alertSuccess(data?.message ?? "Proveedor desasignado de la compañía correctamente.");
        dispatch({ type: types.RELOAD_INDEX_PROVIDERS });
      })
      .catch((e) => showTypeErrorFetch(e));
  };

  const deleteProviderClick = (provider_id) => {
    confirmDelete(
      "¿Seguro quieres eliminar este proveedor?",
      "Esta acción no se puede deshacer.",
      () => {
        axios()
          .delete(`inventory/deleteProvider/${provider_id}`)
          .then(() => {
            dispatch({ type: types.RELOAD_INDEX_PROVIDERS });
            if (state.edit_provider.id === provider_id) {
              dispatch({ type: types.EDIT_FORM_PROVIDER, payload: INITIAL_STATE.edit_provider });
              dispatch({ type: types.SET_EDIT_FORM_PROVIDER, payload: false });
            }
            alertSuccess("Proveedor eliminado correctamente");
          })
          .catch((e) => showTypeErrorFetch(e));
      }
    );
  };

  return (
    <ProvidersIndexContext.Provider
      value={[
        state,
        dispatch,
        {
          setFieldFormProvider,
          createProviderClick,
          goToProviderEdit,
          editProviderClick,
          assignProviderToCompanyClick,
          unassignProviderToCompanyClick,
          deleteProviderClick,
        },
      ]}
    >
      {children}
    </ProvidersIndexContext.Provider>
  );
};

const useProviders = () => useContext(ProvidersIndexContext)[0];
const useDispatchProvidersIndex = () => useContext(ProvidersIndexContext)[1];
const useActionsProvidersIndex = () => useContext(ProvidersIndexContext)[2];

export { useProviders, useDispatchProvidersIndex, useActionsProvidersIndex };
